import Echo from '@ably/laravel-echo';
import * as Ably from 'ably';
import { App, InjectionKey, provide, Ref } from 'vue';
import { BarcodeListener } from '@/helpers/barcodeListener';
import { GeographyRepository } from '@/helpers/geographyRepository';
import { useBarcodeScannerStore } from '@/stores/barcodeScanner';
import { usePopupNotificationsStore } from '@/stores/overlays/popupNotifications';
import useAnalytics, { Analytics } from './useAnalytics';

interface BroadcastingAuthResponse {
    token: string;
    info?: unknown;
}

const getXsrfTokenFromCookie = (): string => {
    const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
    return (match?.[1] != null && match[1] !== '') ? decodeURIComponent(match[1]) : '';
};

// Override the default Ably auth POST. The combination of @ably/laravel-echo (which sets the
// header as mixed-case `Content-Type`) and ably-js's XHR transport (which then adds a lowercase
// `content-type` default without checking case) causes the browser to send
// `Content-Type: application/json, application/json`, which our WAF flags on every request.
//
// Use plain fetch rather than the shared axios client so the Ably JWT in the response is not
// captured in Sentry breadcrumbs and so a background channel auth response cannot trigger
// success or error toasts via the shared response interceptor. Send X-XSRF-TOKEN so the
// request survives Laravel's CSRF middleware if /broadcasting/auth is ever moved behind it.
const requestBroadcastingAuthToken = async (channelName: string, existingToken: string | null): Promise<BroadcastingAuthResponse> => {
    const response = await fetch('/broadcasting/auth', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': getXsrfTokenFromCookie(),
        },
        body: JSON.stringify({
            channel_name: channelName,
            token: existingToken,
        }),
    });

    if (!response.ok) {
        throw new Error(`Broadcasting auth request failed with status ${response.status}`);
    }

    return response.json();
};

const makeKey = <T>(description: string): InjectionKey<T> => Symbol(description);

export const providerKeys: {
    echo: InjectionKey<Echo>;
    barcodeListener: InjectionKey<BarcodeListener>;
    geographyRepository: InjectionKey<GeographyRepository>;
    analytics: InjectionKey<Analytics>;
    axiosShouldNotify: InjectionKey<Ref<boolean>>;
} = {
    echo: makeKey('echo'),
    barcodeListener: makeKey('barcodeListener'),
    geographyRepository: makeKey('geographyRepository'),
    analytics: makeKey('analytics'),
    axiosShouldNotify: makeKey('axiosShouldNotify'),
};

export default function useProviders(): { provideGeographyRepository: (app: App) => void; provideBarcodeListener: (avgTimeByCharMs?: number) => void; provideEcho: (app: App) => void; provideAnalytics: () => void; } {
    const provideGeographyRepository = (app: App): void => {
        const providedGeographyRepository = new GeographyRepository();

        app.provide(providerKeys.geographyRepository, providedGeographyRepository);
    };

    const provideEcho = (app: App): void => {
        window.Ably = Ably;

        const echo = new Echo({
            broadcaster: 'ably',
            requestTokenFn: requestBroadcastingAuthToken,
        });

        // echo.connector is typed as any, so connector.ably is any and no assertion is needed
        const ablyRealtime: Ably.Realtime = echo.connector.ably;
        const ablyConnection = ablyRealtime.connection;
        const ablyDisconnectedTitle = 'Live updates are unavailable. Please refresh to reconnect.';

        // Notify the user when the Ably connection enters the suspended state, which means
        // it has been unable to reconnect for ~2 minutes and live updates are no longer arriving.
        ablyConnection.on('suspended', () => {
            usePopupNotificationsStore().newPopupNotification({
                type: 'action',
                title: ablyDisconnectedTitle,
                actionText: 'Refresh',
                action: () => window.location.reload(),
                permanent: true,
            });
        });

        // Dismiss the suspended notification if the connection recovers so the toast
        // does not persist as a false alarm after Ably successfully reconnects.
        ablyConnection.on('connected', () => {
            usePopupNotificationsStore().removeNotificationByTitle(ablyDisconnectedTitle);
        });

        app.provide(providerKeys.echo, echo);
    };

    const provideBarcodeListener = (avgTimeByCharMs?: number): void => {
        const barcodeScannerStore = useBarcodeScannerStore();

        const providedBarcodeListener = new BarcodeListener((barcode: string) => {
            barcodeScannerStore.setHardwareScan(barcode);
        });

        providedBarcodeListener.initScanner(avgTimeByCharMs);

        provide(providerKeys.barcodeListener, providedBarcodeListener);
    };

    const provideAnalytics = (): void => {
        const analytics = useAnalytics();

        provide(providerKeys.analytics, analytics);

        analytics.initAnalytics();
    };

    return {
        provideGeographyRepository,
        provideBarcodeListener,
        provideEcho,
        provideAnalytics,
    };
}
